import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Button,
  Box,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import postsServices from "../../../services/api/service/postsService";
import RemoveIcon from "./../../../assets/images/icons/remove-icon.svg";
import { useDispatch } from "react-redux";
import {
  setHorizontalSteps,
  updateFormData,
} from "../../../services/redux/slices/stepperDynamicFormSlice";
import ModalPopup from "../../ui/ModalPopup";
import { PrimaryButton } from "../../ui/Button";
import { store } from "../../../services/redux/store";

const EntranceExamDetailsSection = ({
  fields,
  sectionPrefix,
  values,
  setFieldValue,
  renderField,
  horizontalSteps,
  activeVerticalIndex,
  activeHorizontalIndex,
  formikRef,
  trackingId,
}: any) => {
  const dispatch = useDispatch();
  const radioField = fields.find((f: any) => f.type === "radio");
  const showFields = radioField && values[radioField.name] === "Yes";
  const theme = useTheme();
  const isXsOrSm = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<number[] | null>(
    null
  );

  // Section title logic
  const sectionTitle =
    sectionPrefix === "core"
      ? "Core Entrance Exam Details"
      : sectionPrefix === "language"
        ? "Language Entrance Exam Details"
        : "";

  const addMoreBtnTitle =
    sectionPrefix === "core"
      ? "Add Core Entrance Exam"
      : sectionPrefix === "language"
        ? "Add Language Entrance Exam"
        : "";

  const getExamFieldSequence = (fieldName: string) => {
    if (fieldName.includes("ExamName")) return 0;
    if (fieldName.includes("ScoreReceived")) return 1;
    if (fieldName.includes("ExamScore")) return 2;
    if (fieldName.includes("TotalMarks")) return 3;
    if (fieldName.includes("ScoreExpectedOn")) return 4;
    return 99;
  };

  const coreExamOptions =
    fields.find((f: any) => f.name?.includes("coreExamName"))?.options
      ?.options ?? [];

  const coreScoreOptions =
    fields.find((f: any) => f.name?.includes("coreScoreReceived"))?.options
      ?.options ?? [];

  const languageTypeOptions =
    fields.find((f: any) => f.name?.includes("languageExamName"))?.options
      ?.options ?? [];

  const languageScoreOptions =
    fields.find((f: any) => f.name?.includes("languageScoreReceived"))?.options
      ?.options ?? [];

  const hasExamNameOptions =
    sectionPrefix === "language"
      ? languageTypeOptions.length > 0
      : coreExamOptions.length > 0;

  const coreExamFieldsCount = fields.filter(
    (f: any) => f.name?.includes("coreExamName")
  ).length;
  const languageExamFieldsCount = fields.filter(
    (f: any) => f.name?.includes("languageExamName")
  ).length;
  const isCoreExamLimitReached =
    sectionPrefix === "core" &&
    coreExamOptions.length > 0 &&
    coreExamFieldsCount >= coreExamOptions.length;
  const isLanguageExamLimitReached =
    sectionPrefix === "language" &&
    languageTypeOptions.length > 0 &&
    languageExamFieldsCount >= languageTypeOptions.length;

  // Get the current vertical step object
  const currentVerticalStep =
    horizontalSteps?.[activeHorizontalIndex]?.verticalSteps?.[
    activeVerticalIndex
    ];

  // Get the vertical step id
  const verticalStepId = currentVerticalStep?.id;

  /**
   * Adds a new set of exam fields to the form and updates the backend.
   *
   * - Sets loading state while processing.
   * - Clones and transforms the `examFields` array, appending a unique suffix to each field's `name` property.
   * - Sends the transformed fields to the backend API to store user fields.
   * - On successful response, initializes Formik values for the new fields and updates the Redux store with the new entry.
   * - Handles errors and resets loading state.
   *
   * @async
   * @function
   * @returns {Promise<void>}
   */
  const addExamFields = async () => {
    setIsLoading(true);
    try {
      const nextNum = fields.length + 1;

      // clone existing fields but append suffix
      /**
       * Transforms the `examFields` array by mapping each field object to a new object with updated properties.
       * - Copies all properties from the original field.
       * - Sets the `label` property to a string version of the original label.
       * - Appends `nextNum` as a suffix to the `name` property to ensure uniqueness (useful for Formik forms).
       * - Preserves the `options` property for dropdown fields.
       *
    
       * @param nextNum - Suffix to append to each field's `name` property.
       * @returns A new array of field objects with updated `label`, `name`, and preserved `options`.
       */
      // Example for language section (update for core similarly)
      const apiFields =
        sectionPrefix === "language"
          ? [
            {
              label: "Language Exam Name",
              name: `languageExamName_${nextNum}`,
              type: "dropdown",
              required: true,
              options: {
                options: languageTypeOptions,
              },
            },
            {
              label: "Score Received",
              name: `languageScoreReceived_${nextNum}`,
              type: "dropdown",
              required: true,
              options: {
                options: languageScoreOptions,
              },
            },
            {
              label: "Language Exam Score",
              name: `languageExamScore_${nextNum}`,
              type: "number",
              required: true,
              options: null,
            },
            {
              label: "Language Exam Total Marks",
              name: `languageTotalMarks_${nextNum}`,
              type: "number",
              required: true,
              options: null,  
            },
            {
              label: "Score Expected On",
              name: `languageScoreExpectedOn_${nextNum}`,
              type: "date",
              required: false,
              options: null,
            },
          ]
          : [
            // Example for core section
            {
              label: "Core Exam Name",
              name: `coreExamName_${nextNum}`,
              type: "dropdown",
              required: true,
              options: {
                options: coreExamOptions,
              },
            },
            {
              label: "Score Received",
              name: `coreScoreReceived_${nextNum}`,
              type: "dropdown",
              required: true,
              options: {
                options: coreScoreOptions,
              },
            },
            {
              label: "Core Exam Score",
              name: `coreExamScore_${nextNum}`,
              type: "number",
              required: true,
              options: null,
            },
            {
              label: "Core Exam Total Marks",
              name: `coreTotalMarks_${nextNum}`,
              type: "number",
              required: true,
              options: null,
            },
            {
              label: "Score Expected On",
              name: `coreScoreExpectedOn_${nextNum}`,
              type: "date",
              required: false,
              options: null,
            },
          ];

      // Call the API
      const resp = await postsServices.addPost(
        "api/v1/product/store-user-fields",
        {
          vertical_step_id: verticalStepId,
          fields: apiFields,
          trackingId: trackingId,
        }
      );

      if (resp.data?.status === "success") {
        const newFields = resp.data.data.map((f: any) => ({
          ...f,
          name: f.name, // backend returns correct name
        }));

        // --- PUSH new fields into the correct vertical step (id: 9, Loan Details) in applicant_loan horizontal step in Redux state ---
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          console.log(horizontalSteps, "horizontalSteps");

          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );

          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Instead of hardcoding id === 9, use the current vertical step (activeVerticalIndex)
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = [...targetStep.fields, ...newFields];
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update using setHorizontalSteps action creator
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }

        // initialize Formik values
        newFields.forEach((f: any) => {
          setFieldValue(f.name, "");
        });
      }
    } catch (err) {
      console.error("Add entry failed", err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Removes an entrance exam entry at the specified index.
   *
   * This function performs the following steps:
   * 1. Retrieves the entry at the given index from the `entries` array.
   * 2. Sends a request to delete the associated form field IDs from the backend.
   * 3. If the deletion is successful, clears the corresponding Formik field values.
   * 4. Removes the entry from the local state using Redux dispatch.
   *
   * @param idx - The index of the entrance exam entry to remove.
   * @returns A promise that resolves when the removal process is complete.
   */
  const removeExamFields = async (fieldIds: number[]) => {
    try {
      const resp = await postsServices.addPost(
        "api/v1/product/delete-user-fields",
        {
          form_field_ids: fieldIds,
          trackingId: trackingId,
        }
      );

      if (resp.data?.status === "success") {
        // Remove from Redux: Remove fields from horizontalSteps (vertical step id: 9 in applicant_loan)
        const state = store.getState();
        if (
          state &&
          state.stepper &&
          Array.isArray(state.stepper.horizontalSteps)
        ) {
          const horizontalSteps = [...state.stepper.horizontalSteps];
          const applicantLoanStepIdx = horizontalSteps.findIndex(
            (step: any) => step.stepKey === "applicant_loan"
          );
          if (applicantLoanStepIdx !== -1) {
            const applicantLoanStep = {
              ...horizontalSteps[applicantLoanStepIdx],
            };
            const verticalSteps = [...applicantLoanStep.verticalSteps];
            // Use the current vertical step (activeVerticalIndex) instead of hardcoded id === 9
            const targetVerticalIdx = activeVerticalIndex;
            if (
              typeof targetVerticalIdx === "number" &&
              targetVerticalIdx >= 0 &&
              targetVerticalIdx < verticalSteps.length
            ) {
              const stepBefore = verticalSteps[targetVerticalIdx];
              const removedFields = (stepBefore?.fields ?? []).filter(
                (field: any) => fieldIds?.includes(field.id)
              );

              // Clear Formik + persisted form data for removed rows so duplicate-exam
              // dropdown filtering does not keep treating deleted rows as "still selected".
              const keysToClear = new Set<string>();
              const suffixes = new Set<string>();
              removedFields.forEach((field: any) => {
                if (field?.name) keysToClear.add(field.name);
                const m = String(field.name ?? "").match(/_(\d+)$/);
                if (m) suffixes.add(`_${m[1]}`);
              });
              suffixes.forEach((suffix) => {
                keysToClear.add(`coreExamID${suffix}`);
                keysToClear.add(`coreExamId${suffix}`);
                keysToClear.add(`languageExamID${suffix}`);
                keysToClear.add(`languageExamId${suffix}`);
              });

              const clearPayload: Record<string, string> = {};
              keysToClear.forEach((key) => {
                clearPayload[key] = "";
                setFieldValue(key, "", false);
              });
              if (Object.keys(clearPayload).length > 0) {
                dispatch(updateFormData(clearPayload));
              }

              const targetStep = { ...verticalSteps[targetVerticalIdx] };
              targetStep.fields = targetStep.fields.filter(
                (field: any) => !fieldIds?.includes(field.id)
              );
              verticalSteps[targetVerticalIdx] = targetStep;
              applicantLoanStep.verticalSteps = verticalSteps;
              horizontalSteps[applicantLoanStepIdx] = applicantLoanStep;
              // Dispatch redux update
              dispatch(setHorizontalSteps(horizontalSteps));
            }
          }
        }
      }
    } catch (err) {
      console.error("Delete entry failed", err);
    }
  };

  // Handler for remove button click
  const handleRemoveClick = (ids: number[]) => {
    setPendingRemoveIds(ids);
    setConfirmOpen(true);
  };

  // Handler for confirm in modal
  const handleConfirmRemove = () => {
    if (pendingRemoveIds !== null) {
      removeExamFields(pendingRemoveIds);
    }
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  // Handler for cancel in modal
  const handleCancelRemove = () => {
    setConfirmOpen(false);
    setPendingRemoveIds(null);
  };

  useEffect(() => {
    if (radioField && !values[radioField.name])
      setFieldValue(radioField.name, "Yes");
  }, [radioField, setFieldValue, values]);

  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {isXsOrSm && (
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "15px" }}>
            {sectionTitle}
          </Typography>
        </Grid>
      )}

      {/* radio field */}
      <Grid item xs={12}>
        {renderField && radioField && renderField(radioField)}
      </Grid>

      {/* Section Title */}
      {!isXsOrSm && (
        <Grid container item alignItems={"center"}>
          <Grid item xs={6}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "15px" }}>
              {sectionTitle}
            </Typography>
          </Grid>

          <Grid item xs={6} textAlign={"right"}>
            {/* add more button */}
            {showFields && (
              <Button
                sx={{
                  textTransform: "none",
                  fontFamily: "Jost, sans-serif",
                  fontWeight: 600,
                  fontSize: "14px",
                  lineHeight: "100%",
                  letterSpacing: "0%",
                  color: "#33c2c4",
                  borderColor: "#33c2c4",
                  "&:hover": {
                    backgroundColor: "transparent",
                    borderColor: "#33c2c4",
                    color: "#33c2c4",
                  },
                }}
                onClick={addExamFields}
                disabled={
                  isLoading ||
                  !hasExamNameOptions ||
                  isCoreExamLimitReached ||
                  isLanguageExamLimitReached
                }
              >
                <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
                Add More
              </Button>
            )}
          </Grid>
        </Grid>
      )}

      {/* dynamically added entries */}

      {showFields && (
        <>
          {/* Static core/language fields (first row) with conditional required logic */}
          {(() => {
            const staticFields = fields.filter(
              (f: any) => !f.isRemoved && f.type !== "radio"
            );
            const sortedStaticFields = [...staticFields].sort((a: any, b: any) => {
              const sequenceDiff =
                getExamFieldSequence(a.name) - getExamFieldSequence(b.name);
              if (sequenceDiff !== 0) return sequenceDiff;
              return (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0);
            });
            // For static fields, check Score Received value and set required accordingly
            const updatedFields = sortedStaticFields.map((field: any) => {
              if (field.name === "coreScoreExpectedOn") {
                return { ...field, required: true };
              }
              if (field.name === "languageScoreExpectedOn") {
                return { ...field, required: true };
              }
              // Only apply to core fields
              if (
                field.name === "coreExamScore" ||
                field.name === "coreTotalMarks"
              ) {
                const scoreReceived = values?.coreScoreReceived;
                return { ...field, required: scoreReceived !== "No" };
              }
              // Only apply to language fields
              if (
                field.name === "languageExamScore" ||
                field.name === "languageTotalMarks"
              ) {
                const scoreReceived = values?.languageScoreReceived;
                return { ...field, required: scoreReceived !== "No" };
              }
              return field;
            });
            const visibleFields = updatedFields.filter((field: any) => {
              if (field.name.startsWith("core")) {
                const scoreReceived = values?.coreScoreReceived;
                if (
                  scoreReceived !== "No" &&
                  field.name === "coreScoreExpectedOn"
                ) {
                  return false;
                }
                if (
                  scoreReceived === "No" &&
                  (field.name === "coreExamScore" ||
                    field.name === "coreTotalMarks")
                ) {
                  return false;
                }
              }
              if (field.name.startsWith("language")) {
                const scoreReceived = values?.languageScoreReceived;
                if (
                  scoreReceived !== "No" &&
                  field.name === "languageScoreExpectedOn"
                ) {
                  return false;
                }
                if (
                  scoreReceived === "No" &&
                  (field.name === "languageExamScore" ||
                    field.name === "languageTotalMarks")
                ) {
                  return false;
                }
              }
              return true;
            });
            if (staticFields.length > 0) {
              return (
                <Grid container item spacing={2} alignItems="flex-start">
                  {visibleFields.map((f: any) => (
                    <Grid
                      item
                      xs={12}
                      sm={3}
                      key={f.id}
                      sx={{
                        "& > .MuiGrid-root.MuiGrid-item": {
                          maxWidth: "100%",
                          flexBasis: "100%",
                          paddingLeft: 0,
                          paddingTop: 0,
                        },
                      }}
                    >
                      {renderField && renderField(f)}
                    </Grid>
                  ))}
                </Grid>
              );
            }
            return null;
          })()}

          {/* Dynamic entries: group by suffix, render each group in a row with remove, with conditional required logic */}
          {(() => {
            const dynamicFields = fields.filter((f: any) => f.isRemoved);
            // Group by suffix: e.g. coreExamName_6 => group 6
            const groupMap: { [suffix: string]: any[] } = {};
            dynamicFields.forEach((f: any) => {
              const match = f.name.match(/_(\d+)$/);
              if (match) {
                const suffix = match[1];
                if (!groupMap[suffix]) groupMap[suffix] = [];
                groupMap[suffix].push(f);
              }
            });
            // Sort groups by suffix number
            const sortedSuffixes = Object.keys(groupMap).sort(
              (a, b) => Number(a) - Number(b)
            );
            return sortedSuffixes.map((suffix) => {
              const group = [...groupMap[suffix]].sort((a, b) => {
                const sequenceDiff =
                  getExamFieldSequence(a.name) - getExamFieldSequence(b.name);
                if (sequenceDiff !== 0) return sequenceDiff;
                return (a.fieldOrder ?? 0) - (b.fieldOrder ?? 0);
              });
              // For each group, update required logic for score/marks fields based on corresponding Score Received value
              const updatedGroup = group.map((field: any) => {
                if (field.name === `coreScoreExpectedOn_${suffix}`) {
                  return { ...field, required: true };
                }
                if (field.name === `languageScoreExpectedOn_${suffix}`) {
                  return { ...field, required: true };
                }
                // For core dynamic fields
                if (
                  field.name === `coreExamScore_${suffix}` ||
                  field.name === `coreTotalMarks_${suffix}`
                ) {
                  const scoreReceived = values?.[`coreScoreReceived_${suffix}`];
                  return { ...field, required: scoreReceived !== "No" };
                }
                // For language dynamic fields
                if (
                  field.name === `languageExamScore_${suffix}` ||
                  field.name === `languageTotalMarks_${suffix}`
                ) {
                  const scoreReceived = values?.[`languageScoreReceived_${suffix}`];
                  return { ...field, required: scoreReceived !== "No" };
                }
                return field;
              });
              const visibleGroup = updatedGroup.filter((field: any) => {
                const coreScoreReceived = values?.[`coreScoreReceived_${suffix}`];
                const languageScoreReceived = values?.[`languageScoreReceived_${suffix}`];

                if (
                  field.name === `coreScoreExpectedOn_${suffix}` &&
                  coreScoreReceived !== "No"
                ) {
                  return false;
                }
                if (
                  (field.name === `coreExamScore_${suffix}` ||
                    field.name === `coreTotalMarks_${suffix}`) &&
                  coreScoreReceived === "No"
                ) {
                  return false;
                }
                if (
                  field.name === `languageScoreExpectedOn_${suffix}` &&
                  languageScoreReceived !== "No"
                ) {
                  return false;
                }
                if (
                  (field.name === `languageExamScore_${suffix}` ||
                    field.name === `languageTotalMarks_${suffix}`) &&
                  languageScoreReceived === "No"
                ) {
                  return false;
                }
                return true;
              });
              // Collect all field ids in this group for remove
              const fieldIds = group.map((f: any) => f.id);
              return (
                <React.Fragment key={suffix}>
                  <Grid
                    container
                    item
                    spacing={2}
                    alignItems="flex-start"
                  >
                    {visibleGroup.map((f: any) => (
                      <Grid
                        item
                        xs={12}
                        sm={3}
                        key={f.id}
                        sx={{
                          "& > .MuiGrid-root.MuiGrid-item": {
                            maxWidth: "100%",
                            flexBasis: "100%",
                            paddingLeft: 0,
                            paddingTop: 0,
                          },
                        }}
                      >
                        {renderField && renderField(f)}
                      </Grid>
                    ))}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={12}
                      sx={{ textAlign: "left" }}
                    >
                      <Box
                        sx={{ display: "inline-flex", cursor: "pointer" }}
                        onClick={() => handleRemoveClick(fieldIds)}
                      >
                        <img src={RemoveIcon} alt="arrow-narrow-right" />
                        <Typography
                          variant="subtitle2"
                          sx={{
                            ml: 1,
                            fontWeight: 600,
                            color: "#FF4C51",
                            fontSize: "13px",
                          }}
                        >
                          Remove
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </React.Fragment>
              );
            });
          })()}
        </>
      )}

      {isXsOrSm && showFields && (
        <Grid item xs={12}>
          <Button
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#33c2c4",
              border: "1px solid #33c2c4",
              textAlign: "center",
              width: "100%",
            }}
            onClick={addExamFields}
            disabled={isLoading || !hasExamNameOptions}
          >
            <AddIcon sx={{ mr: 1, fontSize: "20px" }} />
            {addMoreBtnTitle}
          </Button>
        </Grid>
      )}

      {/* Confirmation Modal */}
      <ModalPopup
        isOpen={confirmOpen}
        onClose={handleCancelRemove}
        subTitle={
          sectionPrefix === "core"
            ? "Remove Core Entrance Exam"
            : sectionPrefix === "language"
              ? "Remove Language Entrance Exam"
              : "Remove Entrance Exam"
        }
      >
        <Box sx={{ p: 2 }}>
          <Typography>
            Are you sure you want to remove this exam entry? This action cannot
            be undone.
          </Typography>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", mt: 3, gap: 2 }}
          >
            <Button
              onClick={handleCancelRemove}
              color="primary"
              size="small"
              variant="outlined"
            >
              Cancel
            </Button>
            <PrimaryButton onClick={handleConfirmRemove} variant="contained">
              Confirm
            </PrimaryButton>
          </Box>
        </Box>
      </ModalPopup>
    </Grid>
  );
};

export default EntranceExamDetailsSection;
